import { z } from 'zod';
import { IdSchema, RoleSchema } from './common.schema';
import { NotificationSchema } from './activity.schema';
import { NOTIFICATION_CATEGORIES } from '../../src/domain/notification/notificationCatalog';

/**
 * What a person chose for themselves, kept on their account, and what
 * management decided about who is notified of what. The request of
 * 7 October 2026, sections 1 to 3.
 */

// ------------------------------------------------------------------ tables

/** A table's layout as it is kept: column keys in order, the ones left out, the rows a page. */
export const TableLayoutSchema = z.object({
  order: z.array(z.string()),
  hidden: z.array(z.string()),
  /** A whole number from 1 to 200; null for the table's own. */
  pageSize: z.number().int().nullable(),
});

export const SavedTableLayoutSchema = TableLayoutSchema.extend({
  /** The table's key, as the screen that draws it names it: `orders`, `stage:finalcheck`. */
  table: z.string(),
});

export const SavedTableLayoutListSchema = z.object({ items: z.array(SavedTableLayoutSchema) });

// ------------------------------------------------------------- notifications

export const NotificationCategorySchema = z.enum(NOTIFICATION_CATEGORIES);

export const NotificationCategoryInfoSchema = z.object({
  key: NotificationCategorySchema,
  label: z.string(),
});

/** One kind of notification, as the settings screens list it. */
export const NotificationTypeInfoSchema = z.object({
  key: z.string(),
  category: NotificationCategorySchema,
  title: z.string(),
  /** When it is sent, in a line. */
  about: z.string(),
});

/** Where one type stands for a role or for a person. */
export const NotificationRuleSchema = z.object({
  type: z.string(),
  receives: z.boolean(),
  inApp: z.boolean(),
  push: z.boolean(),
  /** Whoever receives it may not turn it off. Never true for a type that is not received. */
  mandatory: z.boolean(),
  /**
   * Different from where it would stand untouched: the catalogue for a role,
   * the role for a person.
   */
  changed: z.boolean(),
});

export const RoleNotificationSettingsSchema = z.object({
  role: RoleSchema,
  label: z.string(),
  rules: z.array(NotificationRuleSchema),
});

/** `GET /notification-settings`: the catalogue, and every role's rules. */
export const NotificationSettingsSchema = z.object({
  categories: z.array(NotificationCategoryInfoSchema),
  types: z.array(NotificationTypeInfoSchema),
  roles: z.array(RoleNotificationSettingsSchema),
});

/** One person's rules, beside their role's, which is what Reset goes back to. */
export const UserNotificationSettingsSchema = z.object({
  userId: IdSchema,
  name: z.string(),
  role: RoleSchema,
  roleLabel: z.string(),
  rules: z.array(NotificationRuleSchema),
  roleRules: z.array(NotificationRuleSchema),
});

/** What management sends to set a role's rules or a person's. Only the types sent change. */
export const SaveNotificationRulesSchema = z.object({
  rules: z.array(
    z.object({
      type: z.string(),
      receives: z.boolean(),
      inApp: z.boolean(),
      push: z.boolean(),
      mandatory: z.boolean(),
    }),
  ),
});

/** One type the caller receives, with what they chose about it. */
export const MyNotificationTypeSchema = NotificationTypeInfoSchema.extend({
  inApp: z.boolean(),
  push: z.boolean(),
  mandatory: z.boolean(),
  /** Not turned off. Always true for a mandatory type. */
  on: z.boolean(),
  /** Shown in the Notifications section of the caller's home screen. */
  home: z.boolean(),
});

/** `GET /preferences/notifications`. */
export const MyNotificationsSchema = z.object({
  categories: z.array(NotificationCategoryInfoSchema),
  /** False for somebody who never chose what their home screen shows, and so sees every type. */
  homeChosen: z.boolean(),
  types: z.array(MyNotificationTypeSchema),
});

/** `PUT /preferences/notifications`. A null home list is every type the caller receives. */
export const SaveMyNotificationsSchema = z.object({
  off: z.array(z.string()),
  home: z.array(z.string()).nullable(),
});

/** `GET /notifications/summary`: what the caller has not read, in all and in each category. */
export const NotificationSummarySchema = z.object({
  unread: z.number().int().nonnegative(),
  byCategory: z.record(NotificationCategorySchema, z.number().int().nonnegative()),
});

/**
 * `GET /notifications/push`: what the caller's device is to show, and the
 * moment to ask from the next time. `next` is exact where a row's `createdAt`
 * may be written to the second; null when there is nothing new.
 */
export const PushNotificationsSchema = z.object({
  items: z.array(NotificationSchema),
  next: z.string().nullable().optional(),
});

export type SavedTableLayout = z.infer<typeof SavedTableLayoutSchema>;
export type NotificationSettingsOverview = z.infer<typeof NotificationSettingsSchema>;
export type RoleNotificationSettings = z.infer<typeof RoleNotificationSettingsSchema>;
export type UserNotificationSettings = z.infer<typeof UserNotificationSettingsSchema>;
export type NotificationRuleRow = z.infer<typeof NotificationRuleSchema>;
export type MyNotifications = z.infer<typeof MyNotificationsSchema>;
export type MyNotificationType = z.infer<typeof MyNotificationTypeSchema>;
export type NotificationSummary = z.infer<typeof NotificationSummarySchema>;
